import { formControl, formGroup, Validators } from '@al00x/forms';
import { Component, inject, signal } from '@angular/core';
import { SupportApiService } from '@core';
import { SupportModel } from '@models';
import { MainLayoutService } from '../../../layout';
import { CommonModule } from '@angular/common';
import { CacFieldComponent, CacButtonComponent, ButtonClickEvent } from '@controladad/ng-base';
import { QuillModule } from 'ngx-quill';
import { ReactiveFormsModule } from '@angular/forms';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { finalize } from 'rxjs';

@Component({
  selector: 'lib-support',
  imports: [
    CommonModule,
    CacFieldComponent,
    CacButtonComponent,
    QuillModule,
    ReactiveFormsModule,
    MatProgressBarModule,
  ],
  templateUrl: './support.component.html',
  styleUrl: './support.component.scss',
  standalone: true,
})
export class SupportComponent {
  readonly mainLayoutService = inject(MainLayoutService);
  readonly supportApiService = inject(SupportApiService);

  isLoadingData = signal(true);

  form = formGroup({
    // Working Hours Section
    shiftEnglish: formControl<string>('', Validators.required),
    shiftArabic: formControl<string>('', Validators.required),

    // Contact Information Section
    email: formControl<string>('', [Validators.email]),
    phoneNumber: formControl<string>('', Validators.required),
    whatsApp: formControl<string>('', Validators.required),

    // Address Section
    addressTextEnglish: formControl<string>(''),
    addressTextArabic: formControl<string>(''),
    lat: formControl<number>(0),
    long: formControl<number>(0),
  });

  constructor() {
    this.loadSupportData();
  }

  activateRTL(editor: any) {
    // Apply formats so typing starts RTL and right-aligned
    editor.format('align', 'right');
    editor.format('direction', 'rtl');
    // Ensure existing content/first line is RTL as well
    try {
      const length = typeof editor.getLength === 'function' ? editor.getLength() : 1;
      if (typeof editor.formatLine === 'function') {
        editor.formatLine(0, length, { direction: 'rtl', align: 'right' });
      }
      if (editor.root) {
        editor.root.setAttribute('dir', 'rtl');
        editor.root.style.direction = 'rtl';
        editor.root.style.textAlign = 'right';
      }
    } catch {}
  }

  private loadSupportData(): void {
    this.isLoadingData.set(true);
    this.supportApiService.getSupport().pipe(finalize(() => this.isLoadingData.set(false))).subscribe({
      next: (support) => {
        this.form.patchValue({
          shiftEnglish: support.shift?.english ?? '',
          shiftArabic: support.shift?.arabic ?? '',
          email: support.email ?? '',
          phoneNumber: support.phoneNumber ?? '',
          whatsApp: support.whatsApp ?? '',
          addressTextEnglish: support.address?.addressText?.english ?? '',
          addressTextArabic: support.address?.addressText?.arabic ?? '',
          lat: support.address?.lat ?? 0,
          long: support.address?.long ?? 0,
        });
      },
      error: (error) => {
        console.error('Error loading support data:', error);
        snackbar$.error('Error loading support data');
      }
    });
  }

  onSubmit(e: ButtonClickEvent): void {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    const formValue = this.form.value;
    const supportData: SupportModel = {
      shift: {
        english: formValue.shiftEnglish!,
        arabic: formValue.shiftArabic!,
      },
      email: formValue.email!,
      countryCode: null,
      whatsAppCountryCode: null,
      phoneNumber: formValue.phoneNumber!,
      whatsApp: formValue.whatsApp!,
      address: {
        addressText: {
          english: formValue.addressTextEnglish || "",
          arabic: formValue.addressTextArabic || "",
        },
        lat: formValue.lat || undefined,
        long: formValue.long || undefined,
      }
    };

    this.supportApiService.updateSupport(supportData)
      .pipe(e.pipe())
      .subscribe({
        next: () => {
          snackbar$.show('Support information updated successfully');
        },
        error: (error) => {
          snackbar$.error('Error updating support information');
          console.error('Error updating support information:', error);
        }
      });
  }
}
